import { Backdrop, Button, Palette, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Typography from '@mui/material/Typography'
import isEqual from 'lodash-es/isEqual'
import React, { useEffect, useRef, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import { useGlobalContext } from '../../Contexts/GlobalContext'
import SignIn from '../../Dialogs/Forms/SignIn'
import SignUp from '../../Dialogs/Forms/SignUp'
import { useScreenSize } from '../../hooks/useScreenSize'
import { UserData } from '../GlobalHeader'
import { analyticsManager } from '../services/analytics/AnalyticsManager'
import { FramedAvatar } from './FramedAvatar'
import GlobalLink from './GlobalLink'
import { MiniProfile } from './MiniProfile'

const DropdownStyle = (palette: Palette) => {
    return {
        boxShadow: 'none',
        '&.dropdown': {
            '& .popper': { zIndex: 1400 },
            backgroundColor: 'transparent',
            '& .popper-menu-list': {
                boxSizing: 'border-box',
                border: 'solid 1px transparent',
                borderRadius: '5px',
                // background: 'linear-gradient(180deg, #17232F 0%, #0E1924 16.06%, #06111C 100%)',
                // backgroundClip: 'padding-box',
                backgroundColor: '#0A1620',
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                height: 'auto',
                // width: '250px',
                minWidth: '270px',
                '&:before': {
                    content: "''",
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: '-1',
                    margin: '-1px',
                    borderRadius: 'inherit',
                    // background: 'linear-gradient(180deg, #16292C 0%, #092028 100%)',
                },

                '& a': {
                    textDecoration: 'none',
                },
                '& .popper-items': {
                    alignItems: 'center',
                    width: '100%',
                    borderLeft: '2px solid transparent',
                    px: 0,
                    '&.active': {
                        color: 'primary.main',
                        borderLeft: '2px solid',
                        borderColor: 'primary.main',

                        '> svg path': {
                            //     fill: palette.primary.main,
                            //     stroke: palette.primary.main,
                        },
                    },
                },
                '& .popper-typo': {
                    fontFamily: 'Fira Sans',
                    fontWeight: 700,
                    fontSize: '20px',
                    textTransform: 'uppercase',
                    letterSpacing: 0,
                    lineHeight: '200%',
                },
            },
        },
    }
}

export type DropdownMenuItem = {
    key?: keyof UserData['newContent']
    new?: boolean
    link?: string
    onClick?: () => void
    title: string | JSX.Element
    icon?: JSX.Element
    target?: string
}

export interface DropdownProps {
    menuButtonTitle?: JSX.Element
    dropdownItems: DropdownMenuItem[]
    userData?: UserData
    variant?: 'default' | 'profile'
    Link: any
    reward?: {
        tier: number
        season: string
    }
}

export default function Dropdown(props: DropdownProps) {
    const { menuButtonTitle, dropdownItems, userData, Link, reward, variant } = props
    const [open, setOpen] = useState(false)
    const anchorRef = useRef<HTMLButtonElement>(null)
    const { registerDialog } = useDialogs()
    const { palette } = useTheme()
    const { isGuest } = useGlobalContext()
    const { isMobile } = useScreenSize()

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen)
    }

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return
        }
        setOpen(false)
    }

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault()
            setOpen(false)
        } else if (event.key === 'Escape') {
            setOpen(false)
        }
    }

    const getActivePage = (link: string) => {
        if (
            isEqual(`${window.location.pathname}${window.location.search}`, link) ||
            isEqual(window.location.pathname, link)
        ) {
            return 'active'
        }
        return ''
    }

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = useRef(open)
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus()
        }

        prevOpen.current = open
    }, [open])

    const onLogIn = () => {
        setOpen(false)
        registerDialog(Dialogs.SIGNIN, <SignIn />)
        analyticsManager.dispatchEvent('openLogin', {
            origin: 'guestProfileDropdown',
        })
    }

    const onSignUp = () => {
        setOpen(false)
        registerDialog(Dialogs.SIGNUP, <SignUp />)
        analyticsManager.dispatchEvent('openSignup', {
            origin: 'guestProfileDropdown',
        })
    }

    return (
        <>
            <Backdrop sx={{ zIndex: 1400 }} open={open} onClick={handleClose} />
            <Paper
                className="dropdown"
                sx={{
                    ...DropdownStyle(palette),
                    ...(variant === 'profile'
                        ? {
                              '.popper': {
                                  transform: {
                                      xs: 'translate3d(2px, 4px, 0px) !important',
                                      md: 'translate3d(-23px, 4px, 0px) !important',
                                  },
                              },
                          }
                        : {}),
                }}
            >
                <Box
                    ref={anchorRef}
                    id="composition-button"
                    aria-controls={open ? 'composition-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2.5,
                        cursor: 'pointer',
                    }}
                >
                    {menuButtonTitle && menuButtonTitle}
                    {variant === 'profile' && (
                        <FramedAvatar
                            avatar={userData?.avatar_url || ''}
                            season={reward?.season}
                            reward={reward?.tier}
                            sx={{
                                zIndex: 1401,
                                height: isMobile ? '36px' : '50px',
                                width: isMobile ? '36px' : '50px',
                            }}
                        />
                    )}
                </Box>
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement={variant === 'profile' ? 'bottom-end' : 'bottom-start'}
                    transition
                    disablePortal
                    className="popper"
                    sx={{
                        '& p': {
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                        },
                        'p > span': {
                            height: '16px',
                            lineHeight: '10px',
                            color: (theme) => theme.palette.error.light,
                            fontFamily: 'Fira Sans',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            padding: '2px 4px',
                            mx: '8px',
                            border: (theme) => `1px solid ${theme.palette.error.light}`,
                        },
                    }}
                >
                    {({ TransitionProps }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: variant === 'profile' ? 'right top' : 'left top',
                            }}
                        >
                            <Paper sx={{ backgroundColor: 'transparent' }}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MenuList
                                        autoFocusItem={open}
                                        id="composition-menu"
                                        aria-labelledby="composition-button"
                                        onKeyDown={handleListKeyDown}
                                        className="popper-menu-list"
                                        sx={{ pt: isMobile ? '3px' : '8px', svg: { minWidth: '50px' } }}
                                    >
                                        {variant === 'profile' && userData && (
                                            <MiniProfile userData={userData} size={isMobile ? 'small' : 'large'} />
                                        )}
                                        {dropdownItems.map((menuItem: DropdownMenuItem, index) => {
                                            if (menuItem.link)
                                                return (
                                                    <GlobalLink
                                                        key={menuItem.key || `dropdown-item-${index}`}
                                                        href={menuItem.link}
                                                        target={menuItem.target ? menuItem.target : '_self'}
                                                        Link={Link}
                                                    >
                                                        <MenuItem
                                                            key={menuItem.link}
                                                            className={`popper-items ${getActivePage(menuItem.link)}`}
                                                            onClick={() => {
                                                                setOpen(false)
                                                            }}
                                                        >
                                                            {menuItem.icon && menuItem.icon}
                                                            <Typography className="popper-typo">
                                                                {menuItem.title} {menuItem.new && <span>New</span>}
                                                            </Typography>
                                                        </MenuItem>
                                                    </GlobalLink>
                                                )
                                            else if (menuItem.onClick)
                                                return (
                                                    <MenuItem
                                                        key={`${menuItem.link}-${index}`}
                                                        className="popper-items"
                                                        onClick={() => {
                                                            menuItem.onClick && menuItem.onClick()
                                                            setOpen(false)
                                                        }}
                                                    >
                                                        {menuItem.icon && menuItem.icon}
                                                        <Typography className="popper-typo">
                                                            {menuItem.title} {menuItem.new && <span>New</span>}
                                                        </Typography>
                                                    </MenuItem>
                                                )
                                        })}
                                        {isGuest && variant === 'profile' && (
                                            <Box
                                                sx={{ button: { minWidth: 'unset', flexGrow: 1 } }}
                                                display="flex"
                                                flexDirection="row"
                                                gap={1}
                                                p={2}
                                            >
                                                <Button variant="contained" onClick={onSignUp}>
                                                    Sign Up
                                                </Button>
                                                <Button variant="outlined" onClick={onLogIn}>
                                                    Log In
                                                </Button>
                                            </Box>
                                        )}
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </Paper>
        </>
    )
}
